import Image from '@tiptap/extension-image'
import { Plugin } from '@tiptap/pm/state'
import { type CommandProps, mergeAttributes } from '@tiptap/core'
import { VueNodeViewRenderer } from '@tiptap/vue-3'
import ky from 'ky'
import { v4 as uuid } from 'uuid'

import { api } from '@/api'
import i18n from '@/i18n'

import { toast } from '@/components/toast'
import { getImageDownloadUrl } from '@/api/image-download-url'

import ImageWidget from '../components/ImageWidget.vue'

import { type UnsavedImageMeta, unsavedImages, readFileAsObjectURL } from './useUnsavedImages'

const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']
const ALLOWED_FILE_SIZE = 5 * 1024 * 1024 // 5MB

export interface SignedUrlResponse {
  uploadUrl: string
  url: string
  maxSize: number

  credential: string
  date: string
  signature: string
  key: string
  policy: string
  algorithm: string
  securityToken: string
}

const getSignedUrl = async (file: File, signingUrl: string) => {
  if (!fileValidator(file)) return ''

  const response = await api.get(signingUrl).json<SignedUrlResponse>()

  if (file.size > response.maxSize)
    throw new Error(i18n.t('modules.comment_editor.file_error_size_template', { name: file.name }))

  return response
}

export const getPrivateSignedUrl = async (file: File) => await getSignedUrl(file, `sign-upload-url?mime=${file.type}`)

export const uploadToSignedUrl = async (file: File, signedUrl: SignedUrlResponse) => {
  // If AWS S3 policy is present, do form-data POST instead of PUT
  if (signedUrl.policy) {
    const form = new FormData()
    form.append('key', signedUrl.key)
    form.append('policy', signedUrl.policy)
    form.append('X-Amz-Signature', signedUrl.signature)
    form.append('X-Amz-Date', signedUrl.date)
    form.append('X-Amz-Credential', signedUrl.credential)
    form.append('X-Amz-Algorithm', signedUrl.algorithm)
    form.append('X-Amz-Security-Token', signedUrl.securityToken)
    form.append('Content-Type', file.type)
    form.append('file', file)
    await ky.post(signedUrl.uploadUrl, { body: form })
  } else {
    await ky.put(signedUrl.uploadUrl, {
      body: file,
      headers: {
        'Content-Type': file.type,
        'X-Goog-Content-Length-Range': `0,${signedUrl.maxSize}`,
      },
    })
  }
}

export const fileValidator = (file: File, options?: { allowedMimeTypes: string[] }) => {
  const { name, type, size } = file
  const validType = (options?.allowedMimeTypes || ALLOWED_MIME_TYPES).includes(type)
  const validSize = size <= ALLOWED_FILE_SIZE

  if (!validType || !validSize) {
    const extension = name.split('.').pop()
    const message = !validType
      ? i18n.t('modules.comment_editor.file_error_format_template', { name, type: type || extension })
      : i18n.t('modules.comment_editor.file_error_size_template', { name })

    toast({
      status: 'error',
      message: message.toString(),
      timeout: 0,
    })
  }

  return validType && validSize
}

declare module '@tiptap/core' {
  interface Commands {
    customImage: {
      fetchSignedUrlData: () => ({ state, dispatch }: CommandProps) => Promise<void>
      setBucketSrcAttribute: () => ({ state, dispatch }: CommandProps) => boolean
      getTemplateImageSrc: () => (p: CommandProps) => Promise<UnsavedImageMeta[]>
      setTemplateImageSrc: (meta: UnsavedImageMeta[]) => (p: CommandProps) => boolean
    }
  }
}

export const CustomImage = Image.extend({
  addNodeView() {
    return VueNodeViewRenderer(ImageWidget as any)
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      'data-klaus-unsaved-image': { default: null },
    }
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      {
        target: '_blank',
        rel: 'noopener noreferrer',
        href: HTMLAttributes.src,
      },
      ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { loading: 'lazy' })],
    ]
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handlePaste(view, event) {
            if (!event.clipboardData) return false

            const items = event.clipboardData.items
            let containsImages = false

            for (const item of items) {
              const image = item.getAsFile()
              if (!image) continue
              containsImages = true
              if (!fileValidator(image)) continue
              event.preventDefault()
              const { schema } = view.state

              const { src, unsavedImageKey } = readFileAsObjectURL(image)
              const node = schema.nodes.image.create({ src, 'data-klaus-unsaved-image': unsavedImageKey })
              const transaction = view.state.tr.replaceSelectionWith(node)
              view.dispatch(transaction)
            }
            return containsImages
          },
        },
      }),
    ]
  },
  addCommands() {
    return {
      fetchSignedUrlData:
        () =>
        async ({ state }) => {
          const promises: Promise<void | string>[] = []

          state.doc.descendants((node) => {
            if (node.type.name === 'image' && node.attrs['data-klaus-unsaved-image']) {
              const img = unsavedImages[node.attrs['data-klaus-unsaved-image']]
              if (!img || img.uploading) return

              img.uploading = true

              promises.push(
                getPrivateSignedUrl(img.file).then((response) => {
                  if (!response) return
                  img.signedUrlResponse = response
                }),
              )
            }
          })

          await Promise.all(promises)
        },
      setBucketSrcAttribute:
        () =>
        ({ state, dispatch }) => {
          const { tr } = state
          let found = false

          state.doc.descendants((node, pos) => {
            if (node.type.name === 'image' && node.attrs['data-klaus-unsaved-image']) {
              found = true

              const image = unsavedImages[node.attrs['data-klaus-unsaved-image']]
              if (!image?.signedUrlResponse?.url) return

              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                src: image.signedUrlResponse.url,
                'data-klaus-unsaved-image': undefined,
              })
            }
          })

          if (found && dispatch) {
            dispatch(tr)
          }

          return found
        },
      getTemplateImageSrc:
        () =>
        async ({ state }) => {
          const promises: Promise<UnsavedImageMeta>[] = []

          state.doc.descendants((node) => {
            if (node.type.name === 'image' && node.attrs.src) {
              const fileName = node.attrs.src.split('/').pop()
              const src = node.attrs.src

              // Skip already processed images
              if (src.startsWith('blob:') || src.startsWith('data:') || node.attrs['data-klaus-unsaved-image']) return

              promises.push(
                getImageDownloadUrl(fileName).then(async ({ downloadUrl }) => {
                  try {
                    const response = await ky.get(downloadUrl)
                    const blob = await response.blob()
                    const file = new File([blob], uuid(), { type: blob.type })
                    return readFileAsObjectURL(file)
                  } catch (error) {
                    console.warn(`Image not found in bucket: ${fileName}`, error)
                    return null
                  }
                }),
              )
            }
          })

          const results = await Promise.all(promises)
          return results.filter((result) => result !== null)
        },
      setTemplateImageSrc:
        (meta) =>
        ({ state, dispatch }) => {
          const { tr } = state
          let found = false
          let i = 0

          state.doc.descendants((node, pos) => {
            if (node.type.name === 'image') {
              found = true
              const { src, unsavedImageKey } = meta[i]
              i++

              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                src,
                'data-klaus-unsaved-image': unsavedImageKey,
              })
            }
          })

          if (found && dispatch) {
            dispatch(tr)
          }

          return found
        },
    }
  },
})
